import { create } from 'zustand';
import { getBookingById } from '@/services/data/bookings';
import { logger } from '@/utils/logger';

interface BookingData {
  product_id: string;
  user_id: string;
  location_id?: string;
  director_id?: string;
  shooting_id?: string;
}

interface BookingState {
  // Booking data by bookingId
  bookings: Record<string, BookingData | null>;
  // Loading states by bookingId
  loading: Record<string, boolean>;
  // Errors by bookingId
  errors: Record<string, Error | null>;
  
  // Actions
  fetchBookingData: (bookingId: string, selectFields?: string) => Promise<void>;
  getBookingData: (bookingId: string) => BookingData | null;
  clearBooking: (bookingId: string) => void;
  clearAllBookings: () => void;
}

export const useBookingStore = create<BookingState>((set, get) => ({
  // Initial state
  bookings: {},
  loading: {},
  errors: {},

  // Fetch booking data from API
  fetchBookingData: async (bookingId: string, selectFields?: string) => {
    // Skip if already loading or already loaded
    const state = get();
    if (state.loading[bookingId] || state.bookings[bookingId] !== undefined) {
      return;
    }

    // Set loading state
    set((state) => ({
      loading: { ...state.loading, [bookingId]: true },
      errors: { ...state.errors, [bookingId]: null },
    }));

    try {
      const result = await getBookingById(bookingId, selectFields);
      
      if (result.success && result.data) {
        const data = result.data as unknown as BookingData;
        set((state) => ({
          bookings: { ...state.bookings, [bookingId]: data },
          loading: { ...state.loading, [bookingId]: false },
          errors: { ...state.errors, [bookingId]: null },
        }));
      } else {
        // Store null to indicate we tried but failed
        set((state) => ({
          bookings: { ...state.bookings, [bookingId]: null },
          loading: { ...state.loading, [bookingId]: false },
          errors: { ...state.errors, [bookingId]: result.error as Error || new Error('Failed to fetch booking') },
        }));
        logger.error('Error fetching booking data:', result.error);
      }
    } catch (error) {
      set((state) => ({
        bookings: { ...state.bookings, [bookingId]: null },
        loading: { ...state.loading, [bookingId]: false },
        errors: { ...state.errors, [bookingId]: error as Error },
      }));
      logger.error('Error fetching booking data:', error);
    }
  },

  // Get booking data (synchronous)
  getBookingData: (bookingId: string) => {
    return get().bookings[bookingId] ?? null;
  },

  // Clear specific booking
  clearBooking: (bookingId: string) => {
    set((state) => {
      const { [bookingId]: _, ...bookings } = state.bookings;
      const { [bookingId]: __, ...loading } = state.loading;
      const { [bookingId]: ___, ...errors } = state.errors;
      return { bookings, loading, errors };
    });
  },

  // Clear all bookings
  clearAllBookings: () => {
    set({ bookings: {}, loading: {}, errors: {} });
  },
}));

